import { Alert, Badge, Button, Card, Group, Stack, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { ProbeModal } from "./ProbeModal";
import { ProvisioningGuideModal } from "./ProvisioningGuideModal";
import { RotationIntervalModal } from "./RotationIntervalModal";
import { TokenDeleteModal } from "./TokenDeleteModal";
import { TokenUpdateModal } from "./TokenUpdateModal";
import {
  describeTokenActionError,
  isTokenGone,
  type TokenAction,
} from "./tokenErrors";
import {
  TOKEN_STATUS_COLOR,
  TOKEN_STATUS_HINT_KEY,
  TOKEN_STATUS_LABEL_KEY,
  tokenCardActions,
} from "./tokenStatus";
import { rotateRepoHostToken, type RepoHostTokenSummary } from "./tokensApi";

export interface TokenCardProps {
  readonly token: RepoHostTokenSummary;
  // Карточка — цель DELETE своей роли (см. deleteTargets).
  readonly deletable: boolean;
  readonly onChanged: () => void;
}

// Форматировать ISO-дату как локальную дату, или «бессрочный».
function formatExpires(expiresAt: string | null, neverLabel: string): string {
  if (expiresAt === null) return neverLabel;
  const date = new Date(expiresAt);
  return date.toLocaleDateString();
}

// Карточка одного токена роли. Набор действий определяется статусом и
// режимом выпуска строки (tokenStatus.ts).
export function TokenCard(props: TokenCardProps): ReactElement {
  const { t } = useTranslation();
  const { token, deletable, onChanged } = props;
  const queryClient = useQueryClient();

  const [probeOpened, setProbeOpened] = useState(false);
  const [updateOpened, setUpdateOpened] = useState(false);
  const [guideOpened, setGuideOpened] = useState(false);
  const [intervalOpened, setIntervalOpened] = useState(false);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const role = token.role_name ?? "connection";

  const actions = tokenCardActions(token, deletable);
  const hasActions = Object.values(actions).some(Boolean);

  const handleActionError = (action: TokenAction, err: Error): void => {
    setActionError(describeTokenActionError(action, err, t));
    if (isTokenGone(err)) {
      onChanged();
    }
  };

  const rotateMutation = useMutation({
    mutationFn: () => rotateRepoHostToken(role),
    onSuccess: () => {
      setActionError(null);
      void queryClient.invalidateQueries({
        queryKey: ["connections", "forgejo", "tokens"],
      });
      onChanged();
    },
    onError: (err) => {
      handleActionError("rotate", err);
    },
  });

  const kindLabel =
    token.binding_kind === "connection"
      ? t("connections.tokens.kindConnection")
      : t("connections.tokens.kindRole", { role: token.role_name ?? "" });

  const modeLabel =
    token.issuance_mode === "user_provided"
      ? t("connections.tokens.modeUserProvided")
      : t("connections.tokens.modeBatonIssued");

  const hintKey = TOKEN_STATUS_HINT_KEY[token.status];

  return (
    <>
      <Card withBorder p="md">
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start">
            <Stack gap={4}>
              <Text size="sm">
                <strong>{t("connections.tokens.labelKind")}:</strong>{" "}
                {kindLabel}
              </Text>
              <Text size="sm">
                <strong>{t("connections.tokens.labelOwner")}:</strong>{" "}
                {token.host_username}
              </Text>
              <Text size="sm">
                <strong>{t("connections.tokens.labelTokenName")}:</strong>{" "}
                {token.token_name}
              </Text>
              {token.declared_scopes.length > 0 ? (
                <Text size="sm">
                  <strong>{t("connections.tokens.labelScopes")}:</strong>{" "}
                  {token.declared_scopes.join(", ")}
                </Text>
              ) : null}
              <Text size="sm">
                <strong>{t("connections.tokens.labelMode")}:</strong>{" "}
                {modeLabel}
              </Text>
              <Text size="sm">
                <strong>{t("connections.tokens.labelExpires")}:</strong>{" "}
                {formatExpires(
                  token.expires_at,
                  t("connections.tokens.expiresNever"),
                )}
              </Text>
              {actions.rotationInterval ? (
                <Text size="sm">
                  <strong>{t("connections.tokens.labelRotation")}:</strong>{" "}
                  {token.rotation_interval_days === null
                    ? t("connections.tokens.rotationNever")
                    : t("connections.tokens.rotationEvery", {
                        days: token.rotation_interval_days,
                      })}
                </Text>
              ) : null}
            </Stack>
            <Badge color={TOKEN_STATUS_COLOR[token.status]}>
              {t(TOKEN_STATUS_LABEL_KEY[token.status])}
            </Badge>
          </Group>

          {hintKey !== undefined ? (
            <Text size="sm" c="dimmed">
              {t(hintKey)}
            </Text>
          ) : null}

          {token.status === "relinquished" ? (
            <Alert
              color="orange"
              title={t("connections.tokens.relinquished.title")}
            >
              {t("connections.tokens.relinquished.text", {
                owner: token.host_username,
                tokenName: token.token_name,
              })}
            </Alert>
          ) : null}

          {actionError !== null ? (
            <Alert color="red" title={t("connections.errorHeading")}>
              {actionError}
            </Alert>
          ) : null}

          {hasActions ? (
            <Group gap="xs">
              {actions.probe ? (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    setProbeOpened(true);
                  }}
                >
                  {t("connections.tokens.probeButton")}
                </Button>
              ) : null}

              {actions.replace ? (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    setUpdateOpened(true);
                  }}
                >
                  {t("connections.tokens.replaceButton")}
                </Button>
              ) : null}

              {actions.rotate ? (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    rotateMutation.mutate();
                  }}
                  loading={rotateMutation.isPending}
                >
                  {t("connections.tokens.rotateButton")}
                </Button>
              ) : null}

              {actions.rotationInterval ? (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    setIntervalOpened(true);
                  }}
                >
                  {t("connections.tokens.rotationIntervalButton")}
                </Button>
              ) : null}

              {actions.guide ? (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => {
                    setGuideOpened(true);
                  }}
                >
                  {t("connections.tokens.guideButton")}
                </Button>
              ) : null}

              {actions.remove ? (
                <Button
                  size="xs"
                  color="red"
                  variant="light"
                  onClick={() => {
                    setDeleteConfirmOpened(true);
                  }}
                >
                  {t("connections.tokens.deleteButton")}
                </Button>
              ) : null}
            </Group>
          ) : null}
        </Stack>
      </Card>

      <ProbeModal
        opened={probeOpened}
        onClose={() => {
          setProbeOpened(false);
        }}
        role={role}
      />

      <ProvisioningGuideModal
        opened={guideOpened}
        onClose={() => {
          setGuideOpened(false);
        }}
        role={role}
      />

      <TokenUpdateModal
        opened={updateOpened}
        onClose={() => {
          setUpdateOpened(false);
        }}
        role={role}
        existing={token}
        onSuccess={onChanged}
      />

      <RotationIntervalModal
        opened={intervalOpened}
        onClose={() => {
          setIntervalOpened(false);
        }}
        role={role}
        current={token.rotation_interval_days}
        onChanged={onChanged}
      />

      <TokenDeleteModal
        opened={deleteConfirmOpened}
        onClose={() => {
          setDeleteConfirmOpened(false);
        }}
        role={role}
        onDeleted={() => {
          setActionError(null);
          setDeleteConfirmOpened(false);
          onChanged();
        }}
        onFailed={(err) => {
          setDeleteConfirmOpened(false);
          handleActionError("remove", err);
        }}
      />
    </>
  );
}
